import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import { useLanguage } from '../LanguageContext';
import CrossIcon from '../icons/CrossIcon';
import TickIcon from '../icons/TickIcon';

const GRID = [
  'FTVEATTOEAGG',
  'ONTAVKAFVFRN',
  'EAORFOIEEOKC',
  'DFFOODHUNTKM',
  'VCKONDOAIOCM',
  'TCATCAFENFIO',
  'VGTENKOOGKEV',
  'AAARDFEHWNKI',
  'EHOEOKARAOKE',
  'OIACAEEOLOSS',
  'INAWCCNNKOFR',
  'OKOOCSINSFAI',
];

const WORD_LIST = [
  {
    word: 'FOODHUNT',
    cells: [
      { r: 3, c: 2 },
      { r: 3, c: 3 },
      { r: 3, c: 4 },
      { r: 3, c: 5 },
      { r: 3, c: 6 },
      { r: 3, c: 7 },
      { r: 3, c: 8 },
      { r: 3, c: 9 },
    ],
  },
  {
    word: 'CATCAFE',
    cells: [
      { r: 5, c: 1 },
      { r: 5, c: 2 },
      { r: 5, c: 3 },
      { r: 5, c: 4 },
      { r: 5, c: 5 },
      { r: 5, c: 6 },
      { r: 5, c: 7 },
    ],
  },
  {
    word: 'MOVIES',
    cells: [
      { r: 4, c: 11 },
      { r: 5, c: 11 },
      { r: 6, c: 11 },
      { r: 7, c: 11 },
      { r: 8, c: 11 },
      { r: 9, c: 11 },
    ],
  },
  {
    word: 'EVENINGWALKS',
    cells: [
      { r: 0, c: 8 },
      { r: 1, c: 8 },
      { r: 2, c: 8 },
      { r: 3, c: 8 },
      { r: 4, c: 8 },
      { r: 5, c: 8 },
      { r: 6, c: 8 },
      { r: 7, c: 8 },
      { r: 8, c: 8 },
      { r: 9, c: 8 },
      { r: 10, c: 8 },
      { r: 11, c: 8 },
    ],
  },
  {
    word: 'KARAOKE',
    cells: [
      { r: 8, c: 5 },
      { r: 8, c: 6 },
      { r: 8, c: 7 },
      { r: 8, c: 8 },
      { r: 8, c: 9 },
      { r: 8, c: 10 },
      { r: 8, c: 11 },
    ],
  },
];

// Helper to check if current selection matches any target word
const checkSelection = (currentSelected) => {
  for (const wordObj of WORD_LIST) {
    if (wordObj.cells.length === currentSelected.length) {
      const allMatchForward = wordObj.cells.every((cell, idx) => {
        return (
          currentSelected[idx].r === cell.r && currentSelected[idx].c === cell.c
        );
      });
      const allMatchBackward = wordObj.cells.every((cell, idx) => {
        const revIdx = wordObj.cells.length - 1 - idx;
        return (
          currentSelected[revIdx].r === cell.r &&
          currentSelected[revIdx].c === cell.c
        );
      });

      if (allMatchForward || allMatchBackward) {
        return wordObj.word;
      }
    }
  }
  return null;
};

export default function FunPage() {
  const [selectedCells, setSelectedCells] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startCell, setStartCell] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  // Refs to read fresh values inside the global mouseup/touchend listener
  const selectedCellsRef = useRef([]);
  const isMouseDownRef = useRef(false);
  const startCellRef = useRef(null);

  useEffect(() => {
    selectedCellsRef.current = selectedCells;
  }, [selectedCells]);

  useEffect(() => {
    isMouseDownRef.current = isMouseDown;
  }, [isMouseDown]);

  useEffect(() => {
    startCellRef.current = startCell;
  }, [startCell]);

  useEffect(() => {
    if (foundWords.length === WORD_LIST.length && WORD_LIST.length > 0) {
      setShowCelebration(true);
    } else {
      setShowCelebration(false);
    }
  }, [foundWords]);

  // Check if a cell is already part of a found word
  const isCellInFoundWord = (r, c) => {
    return foundWords.some((word) => {
      const wordObj = WORD_LIST.find((w) => w.word === word);
      return wordObj?.cells.some((cell) => cell.r === r && cell.c === c);
    });
  };

  // Global mouseup and touchend listener to commit selection on release
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isMouseDownRef.current) {
        const cellsToValidate = selectedCellsRef.current;
        if (cellsToValidate.length > 0) {
          const matchedWord = checkSelection(cellsToValidate);
          if (matchedWord) {
            if (!foundWords.includes(matchedWord)) {
              setFoundWords((prev) => [...prev, matchedWord]);
            }
          }
        }
        setIsMouseDown(false);
        setStartCell(null);
        setSelectedCells([]);
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [foundWords]);

  const handleCellMouseDown = (r, c) => {
    setIsMouseDown(true);
    setStartCell({ r, c });
    setSelectedCells([{ r, c }]);
    document.body.style.overflow = 'hidden';
  };

  const handleCellMouseUp = (r, c) => {
    document.body.style.overflow = '';
  };

  const handleCellHover = (r, c) => {
    if (!isMouseDown || !startCell) return;

    const dr = r - startCell.r;
    const dc = c - startCell.c;
    const isLine = dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc);

    if (isLine) {
      const stepR = Math.sign(dr);
      const stepC = Math.sign(dc);
      const steps = Math.max(Math.abs(dr), Math.abs(dc));
      const cells = [];
      for (let i = 0; i <= steps; i++) {
        cells.push({
          r: startCell.r + i * stepR,
          c: startCell.c + i * stepC,
        });
      }
      setSelectedCells(cells);
    } else {
      // Keep only the starting cell highlighted when dragging outside straight lines
      setSelectedCells([startCell]);
    }
  };

  // Touch support for mobile dragging
  const handleTouchMove = (e) => {
    if (!isMouseDown) return;
    e.preventDefault(); // Stop mobile screen bounce/scroll during drag
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return;
    const cellId = element.getAttribute('data-cell-id');
    if (!cellId) return;
    const [r, c] = cellId.split('-').map(Number);
    handleCellHover(r, c);
  };

  const handleResetGame = () => {
    setSelectedCells([]);
    setFoundWords([]);
  };

  const isCellSelected = (r, c) => {
    return selectedCells.some((cell) => cell.r === r && cell.c === c);
  };

  const isGameCompleted = foundWords.length === WORD_LIST.length;

  const { t } = useLanguage();

  return (
    <div className="w-full min-h-screen">
      <div className="space-y-10 space-y-3">
        {/* Header */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-lg text-content-c1 mb-8 text-center"
        >
          {t('fun.subtitle')}
        </motion.p>

        {/* Game Layout */}
        <div className="flex flex-col gap-8 items-center lg:items-start justify-center">
          {/* Word List Tracker */}
          <div className="card-wedding p-4 w-full space-y-2">
            <h2 className="text-2xl text-content-h2 border-b border-wedding-divider pb-3 text-center">
              {t('fun.words_to_find')}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
              {WORD_LIST.map((item) => {
                const found = foundWords.includes(item.word);
                if (found) {
                  return (
                    <div
                      key={item.word}
                      className="word-item-pending text-content-c2"
                    >
                      <span className="tracking-tight w-full">{item.word}</span>
                      <TickIcon className="w-4 h-4 font-bold text-rose-accent" />
                    </div>
                  );
                }
                return (
                  <div
                    key={item.word}
                    className="text-content-c2 text-divider word-item-pending"
                  >
                    <span className="tracking-tight w-full">
                      {'• '.repeat(item.word.length).trim()}
                    </span>
                    <span className="text-xs w-full text-left mt-0.5">
                      {t('fun.letters', {
                        count: item.word.length,
                      })}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Found Counter */}
            <div className="text-center pt-2 text-xs uppercase tracking-wider text-content-c1">
              {t('fun.progress', {
                count: foundWords.length,
                total: WORD_LIST.length,
              })}
            </div>
          </div>

          {/* Word Search Grid Container */}
          <div className="relative card-wedding p-4 w-full">
            <div
              className="relative grid grid-cols-12 gap-1 select-none text-content-c2"
              onTouchMove={handleTouchMove}
            >
              {/* SVG Highlight Overlay */}
              <svg
                className="absolute inset-0 pointer-events-none w-full h-full z-10"
                viewBox="0 0 12 12"
                preserveAspectRatio="none"
              >
                {/* Found words highlighted in dusty rose */}
                {foundWords.map((word) => {
                  const wordObj = WORD_LIST.find((w) => w.word === word);
                  if (!wordObj) return null;
                  const start = wordObj.cells[0];
                  const end = wordObj.cells[wordObj.cells.length - 1];
                  return (
                    <motion.line
                      key={word}
                      x1={start.c + 0.5}
                      y1={start.r + 0.5}
                      x2={end.c + 0.5}
                      y2={end.r + 0.5}
                      stroke="rgba(200, 125, 135, 0.45)"
                      strokeWidth="0.8"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4 }}
                    />
                  );
                })}

                {/* Active drag selection highlighted in soft blush */}
                {selectedCells.length === 1 && (
                  <circle
                    cx={selectedCells[0].c + 0.5}
                    cy={selectedCells[0].r + 0.5}
                    r="0.4"
                    fill="rgba(200, 125, 135, 0.25)"
                  />
                )}
                {selectedCells.length >= 2 && (
                  <line
                    x1={selectedCells[0].c + 0.5}
                    y1={selectedCells[0].r + 0.5}
                    x2={selectedCells[selectedCells.length - 1].c + 0.5}
                    y2={selectedCells[selectedCells.length - 1].r + 0.5}
                    stroke="rgba(200, 125, 135, 0.25)"
                    strokeWidth="0.8"
                    strokeLinecap="round"
                  />
                )}
              </svg>

              {/* Grid Cells */}
              {GRID.map((row, rIdx) =>
                row.split('').map((char, cIdx) => {
                  const selected = isCellSelected(rIdx, cIdx);
                  const found = isCellInFoundWord(rIdx, cIdx);

                  return (
                    <div
                      key={`${rIdx}-${cIdx}`}
                      data-cell-id={`${rIdx}-${cIdx}`}
                      onMouseEnter={() => handleCellHover(rIdx, cIdx)}
                      onMouseDown={() => handleCellMouseDown(rIdx, cIdx)}
                      onTouchStart={() => handleCellMouseDown(rIdx, cIdx)}
                      onMouseUp={() => handleCellMouseUp(rIdx, cIdx)}
                      onTouchEnd={() => handleCellMouseUp(rIdx, cIdx)}
                      className={`
                        aspect-square flex items-center justify-center text-base sm:text-lg font-bold rounded-full transition-all duration-200 cursor-pointer select-none
                        ${
                          found
                            ? 'word-found'
                            : selected
                              ? 'word-selected'
                              : 'word-default'
                        }
                      `}
                    >
                      {char}
                    </div>
                  );
                }),
              )}
            </div>

            {/* Clear Button (Bottom Right of Grid) */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={handleResetGame}
                className="px-4 py-2 text-xs text-content-c2-b transition-colors uppercase tracking-wider text-rose-accent hover:text-rose-hover"
              >
                {t('fun.reset')}
              </button>
            </div>
          </div>
        </div>

        {/* Celebration Overlay Modal */}
        <AnimatePresence>
          {showCelebration && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCelebration(false)}
              className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm cursor-pointer"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full bg-wedding-blush rounded-3xl p-8 border border-wedding-divider shadow-2xl text-center space-y-4 cursor-default"
              >
                {/* Close Button */}
                <button
                  onClick={() => setShowCelebration(false)}
                  className="absolute top-4 right-4 text-nav-comp-text transition-colors"
                  aria-label="Close celebration message"
                >
                  <CrossIcon className="w-6 h-6" />
                </button>

                <span className="text-5xl block mb-2 select-none">👑💍🎉</span>
                <h2 className="text-3xl text-content-c2-b">
                  {t('fun.completed_title')}
                </h2>
                <p className="text-lg text-content-c2 leading-relaxed">
                  {t('fun.completed_desc')}
                </p>
                <button
                  onClick={() => {
                    handleResetGame();
                    setShowCelebration(false);
                  }}
                  className="btn-wedding-primary"
                >
                  {t('fun.play_again')}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
